import { QuoteType } from "../../types/QuoteType";
import { YesNo } from "../enums";

export interface FixAndFlipQuote {
  quoteName?: string;
  quoteId?: number | null;
  quoteType: QuoteType;
  overrideMaster?: boolean;
  twoFourUnit?: YesNo;
  exceptionPricingOverride?: FixAndFlipOverrideOptions;
  leverage?: ShortTermAdjustmentOptions;
  loanSummary?: LoanSummary;
  loanTerm?: LoanTerm;
  dealTerms?: DealTerms;
  borrowerInfo?: BorrowerInfo;
  username: string;
  sfaccountid?: string;
  sfdealid?: string;
  campaignId?: string;
  campaignCode?: string;
  campaignExpirationDate?: string;
  campaignIncentive?: string;
  campaignName?: string;
  hasSubmittedQuote?: boolean;
  underwriterFields?: ShortTermUnderwriterFields;
  underwriterStatus?: string;
  lenderFinance?: ShortTermLolaFields;
  loanCostOverride?: LoanCostOverrideSTF;
  originationFeeAdjustment?: number;
}

export class LoanSummary {
  loanPurpose?: string = "";
  loanProduct?: string = "";
  propertyAddress?: string = "";
  city?: string = "";
  state?: string = "";
  zip?: string = "";
  propertyType?: string = "";
  formattedAddress?: string = "";
  listedPropertyStatus?: string = "";
}

export class LoanTerm {
  channel?: string = "";
  referralFee?: YesNo = YesNo.No;
  brokerFee?: number = 0;
  loanTerm?: string = "";
  guaranty?: string = "";
  appraisalFee?: number = 0;
  brokerProcessingFee?: number = 0;
  projectInspectionFee?: number = 0;
  internalRefinance?: YesNo = YesNo.No;
  exitFeeCheck?: YesNo = YesNo.No;
  deferredOrigPercent?: number = 0;
  monthsOfInterestReserves?: number;
  documentPrepFee?: number| string = "";
}

export class DealTerms {
  targetCloseDate?: string = "";
  timeCloseToPayoff?: number = 0;
  lesserOfLotOrPurchase?: number = 0;
  rehabConBudget?: number = 0;
  totalPurchasePrice?: number = 0;
  afterCompletedVal?: number = 0;
  refinance?: YesNo = YesNo.No;
  existingDebtStatus?: string = "";
  cashOut?: YesNo = YesNo.Yes;
  heavyRehab?: YesNo = YesNo.No;
  originalPurchaseDate?: string = "";
  payoff?: number = 0;
  buyUpPercentage: number = 0;
  buyDownPercentage: number = 0;
  isZeroOrigFee: YesNo = YesNo.No;
  numOfProperties?: number = 1;
  submittedDate?: string = "";
  dealBudgetPricing?: string = "";
  highRisk?: YesNo = YesNo.No;
  renovationCostsAfterPurchase?: string = "";
}

export class BorrowerInfo {
  primaryGuarantor?: string = "";
  secondaryGuarantor?: string = "";
  borrowerIsReferral: YesNo = YesNo.No;
  borrowingEntity?: string = "";
  fnfCompletedInThreeYears?: number = 0;
  buildsCompletedInThreeYears?: number = 0;
  completedTransactions?: number = 0;
  qualifyingFICO?: number = 0;
  combinedLiquidity?: number = 0;
  usCitizen?: YesNo = YesNo.No;
  borrowerTier: string = "";
  altTier?: YesNo = YesNo.No;
  completedExits?: number = 0;
  wholesalePartner?: string = "";
}

export class FixAndFlipOverrideOptions {
  finalRateOverride?: number | string = "";
  finalOrigFeeOverride?: number | string = "";
  blendedLTCOverride?: number | string = "";
  purchaseLTCOverride?: number | string = "";
  rateReductionOverride?: number | string = 0;
  exceptionType?: string = "";
  arvOverride?: number | string = "";
  commissionReduction?: number | string = 0;
  autoRate?: number | string = 0;
  autoFee?: number | string = 0;
  processingFeeOverride?: number | string = "";
  achRequired?: string = YesNo.Yes;
  achExempt?: string = "Non-exempt";

  constructor(entity: any) {
    if (!entity) return;

    [
      "finalRateOverride",
      "finalOrigFeeOverride",
      "blendedLTCOverride",
      "purchaseLTCOverride",
      "rateReductionOverride",
      "exceptionType",
      "commissionReduction",
      "arvOverride",
      "autoRate",
      "autoFee",
      "processingFeeOverride",
      "achRequired",
      "achExempt",
    ].forEach((prop: string) => {
      const key = prop as keyof FixAndFlipOverrideOptions;
      this[key] = entity[prop];
    });
  }
}

export class ShortTermUnderwriterFields {
  legalFee?: number | string = "";

  constructor(entity: any) {
    if (!entity) return;

    ["legalFee"].forEach((prop: string) => {
      const key = prop as keyof ShortTermUnderwriterFields;
      this[key] = entity[prop];
    });
  }
}

export class ShortTermLolaFields {
  interestRateAddOn?: number | string = 0;
  originationPointsAddOn?: number | string = 0;
  applicationFee?: number | string;
  documentFee?: number | string;
  processingFee?: number | string = 0;
  lenderFinanceAppraisalFee?: number | string;
  rpolt6?: boolean;
  rpogt6?: boolean;
  purchase?: boolean;

  constructor(entity: any) {
    if (!entity) return;

    [
      "interestRateAddOn",
      "originationPointsAddOn",
      "applicationFee",
      "documentFee",
      "processingFee",
      "lenderFinanceAppraisalFee",
      "rpolt6",
      "rpogt6",
      "purchase",
    ].forEach((prop: string) => {
      const key = prop as keyof ShortTermLolaFields;
      this[key] = entity[prop];
    });
  }
}

export class ShortTermAdjustmentOptions {
  adjustedARV?: number | string = "";
  adjustedPurchaseLTC?: number | string = "";
  adjustedBlendedLTC?: number | string = "";

  constructor(entity: any) {
    if (!entity) return;

    ["adjustedARV", "adjustedPurchaseLTC", "adjustedBlendedLTC"].forEach(
      (prop: string) => {
        const key = prop as keyof ShortTermAdjustmentOptions;
        this[key] = entity[prop];
      },
    );
  }
}

export class LoanCostOverrideSTF {
  iREscrowOverrideInt?: number;
  iREscrowOverrideMoneyAmt?: number;

  constructor(entity: any) {
    if (!entity) return;

    ["iREscrowOverrideInt", "iREscrowOverrideMoneyAmt"].forEach(
      (prop: string) => {
        const key = prop as keyof LoanCostOverrideSTF;
        this[key] = entity[prop];
      },
    );
  }
}
